import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useHistory } from 'react-router';
import { debounce } from 'lodash';
import { useTrack } from '@hackler/react-sdk';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { requestEnumToString } from '@utils/enumToString';
import {
  CapaDesignServiceType,
  CapaQuotationServiceType
} from '@generated/graphql';
import * as assets from '@assets';
import useQueryString from '@hooks/useQueryString';
import {
  DesignRequestStageEnum,
  RequestQueryString
} from '@routes/NewRequest/types';
import RequestPreview from '@routes/NewRequest/RequestPreview';
import RequestBottomNav from '@routes/NewRequest/Layout/RequestBottomNav';
import RequestLayout from '../../Layout/RequestCommonLayout';
import PannelLayout from '../../Layout/PannelLayout';
import DesignRequestForm from './DesignRequestForm';
import DesignProcessSelect from './DesignProcessSelect';
import DesignHelperDrawer from '@routes/NewRequest/FormComponents/DesignHelperDrawer';

const steps: DesignRequestStageEnum[] = [
  DesignRequestStageEnum.PROCESS_SELECT,
  DesignRequestStageEnum.DESIGN,
  DesignRequestStageEnum.PREVIEW,
  DesignRequestStageEnum.COMPLETE
];

const DesignRequest: React.FC = () => {
  const [designHelperDrawerOpen, setDesignHelperDrawerOpen] = useState<boolean>(
    false
  );
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const [buttonStateHandler, setButtonStateHandler] = useState<{
    disableHandler: (value: boolean) => void;
  }>({
    disableHandler: (value) => undefined
  });
  const nextButtonAction = useRef<
    Partial<Record<DesignRequestStageEnum, () => void>>
  >({
    [DesignRequestStageEnum.PROCESS_SELECT]: () => undefined,
    [DesignRequestStageEnum.DESIGN]: () => undefined,
    [DesignRequestStageEnum.PREVIEW]: () => undefined
  });
  const currentEventState = useRef<{
    selectedProcess: string;
    exitStage: DesignRequestStageEnum;
  }>({
    selectedProcess: '',
    exitStage: '' as DesignRequestStageEnum
  });
  const history = useHistory<{ fromLandingPage?: boolean }>();
  const track = useTrack();
  const {
    query: { process, stage },
    onAddQueryString
  } = useQueryString<RequestQueryString & { stage: DesignRequestStageEnum }>();
  const processArray = process?.split(',');
  const currentStageIndex = steps.findIndex((step) => step === stage);
  const currentStage = useRef<DesignRequestStageEnum | null>(null);
  const lastStage = useRef<DesignRequestStageEnum | null>(null);
  const currentProcess = useRef<string | null>(null);
  const buttonAction =
    nextButtonAction.current?.[stage as DesignRequestStageEnum];

  const titleRender = useMemo(() => {
    switch (stage) {
      case DesignRequestStageEnum.PROCESS_SELECT:
        return `필요한 작업을
        모두 선택해주세요.`;
      case DesignRequestStageEnum.DESIGN:
        return '요청서를 작성해주세요.';
      case DesignRequestStageEnum.PREVIEW:
        return `마지막으로 요청서를
        확인해주세요.`;
    }
  }, [stage]);

  const contentRender = useMemo(() => {
    switch (stage) {
      case DesignRequestStageEnum.PROCESS_SELECT:
        return (
          <DesignProcessSelect
            setDisabledNextButton={buttonStateHandler.disableHandler}
            nextButtonAction={nextButtonAction}
          />
        );
      case DesignRequestStageEnum.DESIGN:
        return (
          <DesignRequestForm
            setDisabledNextButton={buttonStateHandler.disableHandler}
            nextButtonAction={nextButtonAction}
          />
        );
      case DesignRequestStageEnum.PREVIEW:
        return (
          <RequestPreview
            setDisabledNextButton={buttonStateHandler.disableHandler}
            nextButtonAction={nextButtonAction}
          />
        );
    }
  }, [stage, buttonStateHandler.disableHandler]);

  const extraDescription = useMemo(() => {
    if (!processArray) return;
    if (processArray) {
      const designOrder: string[] = [
        CapaDesignServiceType.ProductDesign,
        CapaDesignServiceType.MechanicalDesign
      ];

      return designOrder
        .reduce((acc, service) => {
          if (processArray.includes(service)) {
            acc.push(requestEnumToString(service as CapaDesignServiceType));
          }
          return acc;
        }, [] as string[])
        .join(', ');
    }
  }, [processArray]);

  const onMoveNextStage = useCallback(() => {
    if (
      stage !== DesignRequestStageEnum.COMPLETE &&
      stage !== DesignRequestStageEnum.PREVIEW
    ) {
      onAddQueryString({
        stage: steps[currentStageIndex + 1]
      });
    }

    if (buttonAction) {
      buttonAction();
    }
  }, [onAddQueryString, currentStageIndex, stage, buttonAction]);

  const onMovePrevStage = useCallback(() => {
    if (stage !== DesignRequestStageEnum.PREVIEW) {
      if (buttonAction) {
        buttonAction();
      }
    }

    if (currentStageIndex === 0) {
      history.push('/request');
    } else {
      onAddQueryString({
        stage: steps[currentStageIndex - 1]
      });
    }
  }, [currentStageIndex, onAddQueryString, history, stage, buttonAction]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (stage) {
      currentStage.current = stage;
    }
  }, [stage]);

  useEffect(() => {
    if (process) {
      currentProcess.current = process;
    }
  }, [process]);

  useEffect(() => {
    const nextButton =
      nextButtonAction.current?.[stage as DesignRequestStageEnum];
    return () => {
      if (stage !== DesignRequestStageEnum.PREVIEW) {
        if (nextButton) {
          nextButton();
        }
      }
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    currentEventState.current = {
      exitStage: !lastStage.current
        ? ('' as DesignRequestStageEnum)
        : lastStage.current,
      selectedProcess: (process ?? '').replace(/CIRCUIT_/g, '')
    };
  }, [process]);

  useEffect(() => {
    if (stage) {
      if (!lastStage.current) {
        lastStage.current = stage;
      }

      if (
        steps.findIndex((step) => step === stage) >
        steps.findIndex((step) => step === lastStage.current)
      ) {
        lastStage.current = stage;
      }
    }
  }, [stage]);

  useEffect(() => {
    const current = currentStage.current;

    return () => {
      if (
        current &&
        current !== DesignRequestStageEnum.COMPLETE &&
        lastStage.current !== DesignRequestStageEnum.COMPLETE
      ) {
        track({
          key: 'exit',
          properties: {
            serviceType: CapaQuotationServiceType.Design,
            exitStage: lastStage.current ?? '',
            selectedProcess: currentProcess.current ?? ''
          }
        });
      }
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const current = currentStage.current;

    const sendTrackingData = () => {
      if (
        current &&
        current !== DesignRequestStageEnum.COMPLETE &&
        lastStage.current !== DesignRequestStageEnum.COMPLETE
      ) {
        track({
          key: 'exit',
          properties: {
            serviceType: CapaQuotationServiceType.Design,
            exitStage: lastStage.current ?? '',
            selectedProcess: currentProcess.current ?? ''
          }
        });
      }
    };

    window.addEventListener('beforeunload', sendTrackingData);

    return () => {
      window.removeEventListener('beforeunload', sendTrackingData);
    };
  });

  return (
    <RequestLayout
      matches={matches}
      title={titleRender}
      titleBackgroundImage={
        matches ? assets.ServiceType_Md : assets.DesignReqeustAsideBackground
      }
      serviceExtraDescription={
        stage !== DesignRequestStageEnum.PROCESS_SELECT
          ? extraDescription
          : undefined
      }
      subButtonLabel={
        stage === DesignRequestStageEnum.PROCESS_SELECT
          ? '제품디자인/기구설계가 궁금하다면?'
          : undefined
      }
      onClickSubButton={
        stage === DesignRequestStageEnum.PROCESS_SELECT
          ? () => setDesignHelperDrawerOpen(true)
          : undefined
      }
      onClickClose={() => history.push('/request')}
    >
      <DesignHelperDrawer
        open={designHelperDrawerOpen}
        onClose={() => setDesignHelperDrawerOpen(false)}
        matches={matches}
      />
      <PannelLayout
        content={contentRender}
        bottomNav={
          stage && (
            <RequestBottomNav
              steps={steps}
              stage={stage}
              setButtonStateHandler={setButtonStateHandler}
              onClickNext={debounce(onMoveNextStage, 50)}
              onClickBack={debounce(onMovePrevStage, 50)}
            />
          )
        }
      />
    </RequestLayout>
  );
};

export default DesignRequest;
